import { V2_DOCUMENTS, V2_ENROLLMENT_YEAR } from "./documentV2Config";
import { formatEnrollmentDate } from "../../utils/enrollmentDate";
import content from "./documentV2Content.json";
import "./htmlDocumentV2.css";

function ContractClause({ text }) {
  if (text.startsWith("En caso de reincorporación")) return <strong>{text}</strong>;
  const emphasis = [
    "La no cancelación anual de las cuotas implica la pérdida de la vacante",
    "no se renueva automáticamente.",
  ].find((phrase) => text.includes(phrase));
  if (!emphasis) return text;
  const position = text.indexOf(emphasis);
  if (position === -1) return text;
  return (
    <>
      {text.slice(0, position)}
      <strong>{emphasis}</strong>
      {text.slice(position + emphasis.length)}
    </>
  );
}

function DocumentHeader({ title, documentType = "solicitud", showImages = true }) {
  const portrait =
    documentType === "anexo" ? "/documentos-v3/anexo-1-2.png" : "/documentos-v3/solicitud-1-21.png";
  const crest =
    documentType === "anexo" ? "/documentos-v3/anexo-1-3.png" : "/documentos-v3/solicitud-1-20.png";
  return (
    <header className="react-document__header">
      {showImages && <img src={portrait} alt="Madre Cabrini" width="72" height="80" />}
      <div>
        <p>ASOCIACIÓN COLUMBUS</p>
        <strong>INSTITUTO SAGRADO CORAZÓN</strong>
        <span>Avenida Mitre 1222 · Villa Mercedes · San Luis</span>
        {documentType === "solicitud" && showImages && (
          <span className="react-document__emails">
            e-mail: adminis.sc@mcabrini.org.ar · adm.sc@mcabrini.org.ar
          </span>
        )}
        <h1>{title}</h1>
        <b>Ciclo lectivo {V2_ENROLLMENT_YEAR}</b>
      </div>
      {showImages && (
        <img src={crest} alt="Escudo del Instituto Sagrado Corazón" width="72" height="80" />
      )}
    </header>
  );
}

function Signature({ label = "Firma del responsable" }) {
  return (
    <div className="react-document__signature">
      <span />
      {label}
    </div>
  );
}

function Field({ field, values, label, inline = false }) {
  const value = values[field.id];
  const shortLabels = {
    name: "Apellido y nombre",
    relationship: "Carácter de representación",
    profession: "Profesión",
    workplace: "Lugar de trabajo",
    phone: "Teléfono",
    dni: "DNI",
    email: "Correo electrónico",
    clarification: "Aclaración y DNI",
    enrollment_date: "Fecha de inscripción",
  };
  const suffix = field.id.replace(/^(guardian|authorized)_\d_/, "").replace(/^guardian_/, "");
  const displayLabel =
    label ||
    shortLabels[suffix] ||
    (/^secondary_division_/.test(field.id)
      ? `${field.id.slice(-1)}° año · División`
      : /_enrollment_date$/.test(field.id)
      ? "Fecha de inscripción"
      : field.label);
  const text = field.id.includes("date") ? formatEnrollmentDate(value || "") : value || "";
  return (
    <div
      className={`react-document__field${inline ? " react-document__field--inline" : ""}`}
      data-field-id={field.id}
    >
      {field.type === "checkbox" ? (
        <>
          <span
            className="react-document__checkbox"
            aria-label={`${field.label}: ${value ? "seleccionado" : "sin seleccionar"}`}
          >
            {value ? "×" : ""}
          </span>
          <span>{displayLabel}</span>
        </>
      ) : (
        <>
          <span className="react-document__label">{displayLabel}</span>
          <span className="react-document__value">{text || "\u00a0"}</span>
        </>
      )}
    </div>
  );
}

function FieldGroup({ title, fields, values, className = "" }) {
  return (
    <section className={`react-document__section ${className}`}>
      <h2>{title}</h2>
      <div className="react-document__grid">
        {fields.map((field) => (
          <Field key={field.id} field={field} values={values} />
        ))}
      </div>
    </section>
  );
}

function Solicitud({ fields, values }) {
  const select = (pattern) => fields.filter((field) => pattern.test(field.id));
  return (
    <>
      <article className="react-document__page" aria-label="Solicitud de inscripción, página 1">
        <DocumentHeader title="SOLICITUD DE INSCRIPCIÓN" />
        <FieldGroup
          title="Datos del/la alumno/a"
          fields={select(/^(student_|birth_|nationality|blood_group)/)}
          values={values}
        />
        <FieldGroup
          title="Domicilio y contacto"
          fields={select(/^(address|locality|postal_code|province|home_phone|alternate_phone)/)}
          values={values}
          className="react-document__contact"
        />
        <div className="react-document__levels">
          {[
            ["Inicial", /^initial_/],
            ["Primaria", /^primary_/],
            ["Secundaria", /^secondary_/],
          ].map(([name, pattern]) => (
            <FieldGroup
              key={name}
              title={`Educación ${name}`}
              fields={select(pattern)}
              values={values}
            />
          ))}
        </div>
        <aside className="react-document__school">
          A completar por el establecimiento: <span>Turno mañana □</span>
          <span>Turno tarde □</span>
        </aside>
        <section className="react-document__section">
          <h2>Sacramentos del alumno/a</h2>
          <p className="react-document__note">
            Completar solamente los alumnos ingresantes nuevos al establecimiento, según el nivel
            que corresponda.
          </p>
          <table className="react-document__table">
            <thead>
              <tr>
                <th>Sacramento</th>
                <th>Lugar</th>
                <th>Localidad</th>
                <th>Provincia</th>
                <th>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Bautismo", "baptism"],
                ["Primera comunión", "communion"],
                ["Confirmación", "confirmation"],
              ].map(([name, prefix]) => (
                <tr key={prefix}>
                  <th>{name}</th>
                  {["place", "locality", "province", "date"].map((suffix) => (
                    <td key={suffix}>
                      <Field
                        field={fields.find((f) => f.id === `${prefix}_${suffix}`)}
                        values={values}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <div className="react-document__responsibles">
          {[1, 2].map((index) => (
            <FieldGroup
              key={index}
              title={`Responsable ${index}`}
              fields={select(new RegExp(`^guardian_${index}_`))}
              values={values}
            />
          ))}
        </div>
        <p className="react-document__note">
          Solicitamos comunicar a la brevedad cualquier cambio en los responsables, teléfonos o
          domicilios.
        </p>
        <p className="react-document__declaration">
          Declaro que todos los datos contenidos en la presente son exactos y sin enmiendas.
        </p>
        <div className="react-document__signatures">
          <Signature label="Firma del responsable 1" />
          <Signature label="Firma del responsable 2" />
        </div>
        <footer>
          La presente solicitud de inscripción tendrá validez con el sello del establecimiento.
        </footer>
      </article>
      <article
        className="react-document__page react-document__contract"
        aria-label="Contrato de adhesión, página 2"
      >
        <DocumentHeader title="CONTRATO DE ADHESIÓN" showImages={false} />
        <p>
          Entre el Instituto Sagrado Corazón, con domicilio en Avenida Mitre N° 1222 de Villa
          Mercedes (S.L.), por una parte, en adelante el Instituto, y por la otra el representante
          del/la alumno/a, convienen en celebrar el presente contrato de adhesión, sujeto a las
          cláusulas generales y particulares:
        </p>
        <FieldGroup
          title="Datos del contrato"
          fields={select(/^contract_(guardian|student|course|division)$/)}
          values={values}
        />
        <h2>CLÁUSULAS GENERALES</h2>
        <ol>
          {content.generalClauses.map((clause, index) => (
            <li key={index}>
              {index === 0 ? (
                `El Responsable contrata para el ciclo lectivo ${V2_ENROLLMENT_YEAR} los servicios educativos que brinda el Instituto, al que asistirá el alumno al frente indicado del curso y división arriba consignados.`
              ) : (
                <ContractClause
                  text={clause.replace(
                    /ciclo lectivo \d{4}/g,
                    `ciclo lectivo ${V2_ENROLLMENT_YEAR}`
                  )}
                />
              )}
            </li>
          ))}
        </ol>
        <h2>CLÁUSULAS PARTICULARES</h2>
        <ol start="10">
          {content.particularClauses.map((clause, index) => (
            <li key={index}>{clause}</li>
          ))}
        </ol>
        <div className="react-document__signatures">
          <Signature />
          <div className="react-document__grid">
            {select(/^contract_(clarification|dni|relationship)$/).map((field) => (
              <Field key={field.id} field={field} values={values} />
            ))}
          </div>
        </div>
        <footer>
          La presente solicitud de inscripción tendrá validez con el sello del establecimiento.
        </footer>
      </article>
    </>
  );
}

function Anexo({ fields, values }) {
  const select = (pattern) => fields.filter((field) => pattern.test(field.id));
  return (
    <article
      className="react-document__page react-document__anexo"
      aria-label="Anexo de inscripción, página 1"
    >
      <DocumentHeader
        title="ANEXO SOLICITUD DE INSCRIPCIÓN · CONTRATO DE ADHESIÓN"
        documentType="anexo"
      />
      <FieldGroup title="Datos del alumno/a" fields={select(/^student_/)} values={values} />
      <section className="react-document__section">
        <h2>Obligaciones del responsable</h2>
        <p>Como primer integrante y educador del alumno/a, se compromete a:</p>
        <ol>
          {content.obligations.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ol>
      </section>
      <p className="react-document__declaration">
        <strong>
          8. En el caso que no pueda retirar personalmente al alumno, autorizo para el retiro del
          mismo a las personas que se detallan a continuación:
        </strong>
      </p>
      <div className="react-document__responsibles">
        {[1, 2].map((index) => (
          <FieldGroup
            key={index}
            title={`Persona autorizada ${index}`}
            fields={select(new RegExp(`^authorized_${index}_`))}
            values={values}
          />
        ))}
      </div>
      <FieldGroup title="Transporte escolar" fields={select(/^transport_/)} values={values} />
      <div className="react-document__responsibles">
        {[1, 2].map((index) => (
          <section className="react-document__section" key={index}>
            <h2>Responsable {index}</h2>
            <Signature />
            <div className="react-document__grid">
              {select(index === 1 ? /^guardian_(clarification|relationship)$/ : /^guardian_2_/).map(
                (field) => (
                  <Field key={field.id} field={field} values={values} />
                )
              )}
            </div>
          </section>
        ))}
      </div>
    </article>
  );
}

export default function HtmlEnrollmentDocument({
  documentType,
  values = {},
  fields = V2_DOCUMENTS[documentType].fields,
}) {
  return (
    <section className={`react-document react-document--${documentType}`}>
      {documentType === "solicitud" ? (
        <Solicitud fields={fields} values={values} />
      ) : (
        <Anexo fields={fields} values={values} />
      )}
    </section>
  );
}
